import { DEFAULT_LOCALE, LOCALES, type Locale } from './catalogs';

// ═══════════════════════════════════════════════════════════════════
// WHERE THE LANGUAGE CHOICE IS KEPT (#310a, #310).
//
// A NARROW SEAM, deliberately — and it has now widened by exactly one step,
// which is what it was written for. There are TWO places a choice can live:
//
//   the BROWSER   `localStorage['starpoint.locale']`. Whoever is sitting at
//                 this machine, signed in or not. The only home there was
//                 until the account model existed.
//   the ACCOUNT   a column on the signed-in user (`users.locale`, #310), so
//                 the choice follows the person to another machine.
//
// THE ACCOUNT WINS WHILE THERE IS ONE. An account setting is the more
// specific statement of intent: somebody who chose English on their own login
// means it on every machine they sign in to, and a browser default set by
// whoever used the club's desk computer last is not an argument against that.
//
// AND IT DOES NOT LEAK BACK. A choice made while signed in is written to the
// account and NOT to this browser, so the club's shared desk machine does not
// inherit the language of the last administrator who used it. Signing out
// restores whatever this browser holds. The portal's token lives in memory
// only, so a reload is a sign-out anyway — there is no state in between for
// the two to disagree about.
//
// WHO IS «THE USER». A club/staff account is a `User` row, and that is what
// this covers. A MEMBER is a `Player` with a member session and no `User` row
// at all: there is nowhere to put their preference and inventing one is not
// this change's call, so members still use the browser. See
// docs/architecture/ARCHITECTURE.md § Internationalisation.
//
// IT IS A PREFERENCE, NOT DATA. Losing it is a mild annoyance, not a defect:
// a private window, cleared site data or a second machine simply starts on
// Norwegian again. So every access is wrapped — some browsers throw on
// `localStorage` rather than returning null — and a failure is silent. The
// account path holds to the same rule: a write the server refused does not
// undo the switch the person just made, it only fails to follow them.
// ═══════════════════════════════════════════════════════════════════

/** Prefixed, so it cannot collide with anything the app stores per club. */
const KEY = 'starpoint.locale';

export const isLocale = (v: unknown): v is Locale => LOCALES.includes(v as Locale);

/**
 * The stored choice, or the default.
 *
 * An unrecognised value is treated as absent rather than passed on: a locale
 * this build does not have would otherwise reach the catalog lookup and fall
 * back on every single string, which looks like a broken translation instead
 * of a stale preference.
 */
export function readLocale(): Locale {
  try {
    const raw = globalThis.localStorage?.getItem(KEY);
    return isLocale(raw) ? raw : DEFAULT_LOCALE;
  } catch {
    return DEFAULT_LOCALE;
  }
}

export function writeLocale(locale: Locale): void {
  try {
    globalThis.localStorage?.setItem(KEY, locale);
  } catch {
    /* a preference that cannot be stored is still usable for this session */
  }
}

// ── THE ACCOUNT, WHEN THERE IS ONE ───────────────────────────────────

/**
 * Somewhere a signed-in account's choice is kept.
 *
 * An interface rather than a direct call to the API client so this module
 * stays what it has always been — the one place that knows WHERE a locale
 * goes — without also becoming a module that knows about sessions and HTTP.
 */
export interface AccountLocaleStore {
  /**
   * Persist. Resolves when the account has it, or when the attempt failed;
   * it never rejects, because a dropdown handler is not a place to catch.
   */
  save(locale: Locale): Promise<void>;
}

/**
 * Module-level, like `clubSession` and `Storage`, and for the same reason:
 * there is one signed-in account per browser tab, and threading it through
 * React would put the same singleton behind a prop that can only ever have
 * one value.
 */
let account: AccountLocaleStore | null = null;

/**
 * Hand the preference to a signed-in account, or (`null`) back to the browser.
 *
 * Called on sign-in and sign-out. Passing `null` is not optional housekeeping:
 * a store left bound after sign-out would write the NEXT person's choice to
 * the previous person's account through a session that no longer exists.
 */
export function bindAccountLocale(store: AccountLocaleStore | null): void {
  account = store;
}

/** Is an account currently the owner of this preference? Tests and callers ask. */
export function accountOwnsLocale(): boolean {
  return account !== null;
}

/**
 * Persist a CHOSEN locale wherever it belongs right now.
 *
 * The one thing above this file that has to be true: a choice is stored
 * before it is shown, so the UI never displays a language that was not
 * written down first. What «written down» means — a row or a browser key —
 * is this module's business and nobody else's.
 */
export async function persistLocale(locale: Locale): Promise<void> {
  if (account) {
    try {
      await account.save(locale);
    } catch {
      // A preference that cannot be stored is still usable for this session —
      // the same rule the browser path below has always had.
      //
      // The provider also catches (LocaleProvider.tsx), so the language
      // switches either way. That is deliberate belt and braces: this one
      // keeps the failure from travelling, and that one means the guarantee
      // does not depend on this comment staying true.
    }
    return;
  }
  writeLocale(locale);
}

/** Tests only, so one case cannot inherit another's choice. */
export function __clearStoredLocale(): void {
  account = null;
  try {
    globalThis.localStorage?.removeItem(KEY);
  } catch {
    /* nothing to clear */
  }
}
